import React, { useRef } from 'react';
import { TokenPicker, TokenDef } from './TokenPicker';
import { MessageFormatBar } from './MessageFormatBar';
import { LigneNotification } from './LigneNotification';

/**
 * Un email configurable : son interrupteur, son sujet, son message, ses variables, et de quoi se
 * l'envoyer à soi pour voir le rendu.
 *
 * POURQUOI CE COMPOSANT EXISTE. Deux écrans de réglages d'email — « Menus & convives » et « Booking des
 * extras » — avaient été écrits à part, avec leur propre mise en page et leur propre barre de mise en
 * forme. Résultat : tout ce qu'on ajoutait au bloc email commun les contournait. Ils se sont retrouvés
 * sans interrupteur d'activation (signalé par Emeni le 2026-09-18, alors qu'il l'avait déjà demandé),
 * puis sans bouton « Tester l'email » et sans la mise en forme enrichie. Un écran qui a sa propre copie
 * finit toujours par rater le train suivant.
 *
 * Les modèles sont décrits par une simple liste : le composant en déduit les noms des champs
 * (`<clé>_subject`, `<clé>_body`, `notify_<clé>`), ce qui rend impossible d'en ajouter un et d'oublier
 * son interrupteur.
 */

export interface ModeleEmail {
    /** Préfixe des champs : « invitation » donne invitation_subject, invitation_body, notify_invitation. */
    cle: string;
    titre: string;
    /** Quand cet email part, dit en une phrase. */
    description: string;
    tokens: readonly TokenDef[];
}

export type ValeursEmails = Record<string, string | boolean>;

/** L'état de départ d'un écran : tous les champs vides, tous les emails actifs. */
export const valeursVides = (modeles: readonly ModeleEmail[]): ValeursEmails =>
    modeles.reduce((acc, m) => ({
        ...acc, [`${m.cle}_subject`]: '', [`${m.cle}_body`]: '', [`notify_${m.cle}`]: true,
    }), {} as ValeursEmails);

/** Ce que renvoie le serveur, ramené à la forme de l'écran. Une colonne absente vaut « actif ». */
export const valeursDepuisServeur = (modeles: readonly ModeleEmail[], s: any): ValeursEmails =>
    modeles.reduce((acc, m) => ({
        ...acc,
        [`${m.cle}_subject`]: s?.[`${m.cle}_subject`] || '',
        [`${m.cle}_body`]: s?.[`${m.cle}_body`] || '',
        [`notify_${m.cle}`]: s?.[`notify_${m.cle}`] === 0 || s?.[`notify_${m.cle}`] === false ? false : true,
    }), {} as ValeursEmails);

/** Combien d'emails sont éteints — pour le dire en tête de la fenêtre. */
export const nombreEteints = (modeles: readonly ModeleEmail[], valeurs: ValeursEmails): number =>
    modeles.filter((m) => valeurs[`notify_${m.cle}`] === false).length;

export function SectionEmailConfigurable({ modele, valeurs, modifier }: {
    modele: ModeleEmail;
    valeurs: ValeursEmails;
    modifier: (patch: ValeursEmails) => void;
    /** Conservé pour les appelants : la mise en page en lignes n'a plus besoin de savoir qui est premier. */
    premier?: boolean;
}) {
    const corpsRef = useRef<HTMLTextAreaElement>(null);
    const cleSujet = `${modele.cle}_subject`;
    const cleCorps = `${modele.cle}_body`;
    const cleActif = `notify_${modele.cle}`;
    const sujet = String(valeurs[cleSujet] || '');
    const corps = String(valeurs[cleCorps] || '');

    // Une ligne par email (même présentation que Paramètres > Notifications) ; le modèle reste modifiable
    // quand l'email est éteint : seul l'envoi est suspendu, et le serveur le refuse aussi de son côté.
    return (
        <LigneNotification titre={modele.titre} description={modele.description}
            toggle={{ checked: valeurs[cleActif] !== false, onChange: (v) => modifier({ [cleActif]: v }) }}
            sujet={sujet} corps={corps} setSujet={(v) => modifier({ [cleSujet]: v })} setCorps={(v) => modifier({ [cleCorps]: v })}
            editeur={(
                <>
                    <label className="block">
                        <span className="block text-[11px] font-bold uppercase tracking-[.09em] text-content-muted mb-1.5">Objet</span>
                        <input type="text" value={sujet} onChange={(e) => modifier({ [cleSujet]: e.target.value })}
                            className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                    </label>
                    <div>
                        <span className="block text-[11px] font-bold uppercase tracking-[.09em] text-content-muted mb-1.5">Message</span>
                        <MessageFormatBar textareaRef={corpsRef} value={corps} onChange={(v) => modifier({ [cleCorps]: v })} />
                        <textarea ref={corpsRef} value={corps} onChange={(e) => modifier({ [cleCorps]: e.target.value })} rows={12}
                            className="mt-2 w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500 resize-y" />
                    </div>
                    <TokenPicker tokens={modele.tokens as TokenDef[]} textareaRef={corpsRef} value={corps} onChange={(v) => modifier({ [cleCorps]: v })} />
                </>
            )} />
    );
}
